import { API } from '../../shared/constants/constants';
import { normalizeOrganizationQueryName } from '../../shared/requests/organizationNormalization';
import { normalizeExpertId, normalizeExpertQueryParams } from '../../utils/expertNormalization';
import { normalizeServiceId } from '../../shared/utils/serviceLogNormalization';

export const getJournalists = async () => {
  try {
    const res = await API.get(`/journalists`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getExperts = async () => {
  try {
    const res = await API.get(`/experts`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getUnits = async () => {
  try {
    const res = await API.get(`table/units`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

// search for related issues of a contact
export const getRelatedIssues = async (jID) => {
  // URL encod the contactId
  // const encodedID = encodeURIComponent(jID);
  try {
    const res = await API.get(`/issues/filter?journalist=["${jID}"]`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getRelatedIssuesByContactPersonnelID = async (category, jID) => {
  let URL;
  if (category === 'Journalists') {
    URL = `/issues/journalist/${jID}`;
  } else if (category === 'Experts') {
    URL = `/issues/expert/${jID}`;
  } else {
    URL = `/engagements/community-member/${jID}`;
  }
  try {
    const res = await API.get(URL);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getPersonnelByOrg = async (outletID) => {
  const URL = `/journalists?Org=${outletID}`;
  const res = await API.get(URL);
  res.data?.data?.sort((dataA, dataB) => {
    if (dataA.name?.toLowerCase() < dataB.name?.toLowerCase()) {
      return -1;
    }
    if (dataA.name?.toLowerCase() > dataB.name?.toLowerCase()) {
      return 1;
    }
    return 0;
  });
  return res.data;
};

//get all Organizations
export const getOrgs = async () => {
  try {
    const res = await API.get(`/orgs`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getOrgsByCaseInsensitiveName = async (orgName) => {
  const res = await API.get(`/orgs?name=${orgName}&caseInsensitiveName=true`);
  return res.data;
};

export const getDeptsByCaseInsensitiveName = async (deptName) => {
  const res = await API.get(`/depts?name=${deptName}&caseInsensitiveName=true`);
  return res.data;
};

export const getJournalistsByCaseInsensitiveNameAndOrg = async (journalistName, org) => {
  const res = await API.get(`/journalists?name=${journalistName}&Org=${org}&caseInsensitiveName=true`);
  return res.data;
};

export const getExpertsByCaseInsensitiveNameAndUnit = async (expertName, unit) => {
  const normalized = normalizeExpertQueryParams({ name: expertName, unit });
  const nameParam = encodeURIComponent(normalized.name ?? '');
  const unitParam = encodeURIComponent(normalized.unit ?? '');
  const res = await API.get(`/experts?name=${nameParam}&unit=${unitParam}&caseInsensitiveName=true`);
  return res.data;
};

const normalizeMergeOrg = (org) => {
  if (!org) {
    return undefined;
  }
  if (typeof org === 'string') {
    return org;
  }
  const normalized = {
    _id: org._id ?? org.id,
    name: org.name,
    archived: org.archived,
    tenantIdDisc: org.tenantIdDisc,
    customFields: org.customFields,
    __v: org.__v,
  };
  return normalized._id ? normalized : undefined;
};

const normalizeMergeJournalist = (journalist) => {
  if (!journalist) {
    return null;
  }
  if (typeof journalist === 'string') {
    return { _id: journalist, name: '' };
  }
  return {
    _id: journalist._id ?? journalist.id,
    name: journalist.name ?? '',
    Org: normalizeMergeOrg(journalist.Org),
    email: journalist.email,
    phone: journalist.phone,
    cell: journalist.cell,
    notes: journalist.notes,
    archived: journalist.archived,
    tenantIdDisc: journalist.tenantIdDisc,
    customFields: journalist.customFields,
    __v: journalist.__v,
    createdAt: journalist.createdAt,
    updatedAt: journalist.updatedAt,
  };
};

const normalizeMergeExpert = (expert) => {
  if (!expert) {
    return null;
  }
  if (typeof expert === 'string') {
    return { _id: expert, name: '' };
  }
  return {
    _id: expert._id ?? expert.id,
    name: expert.name ?? '',
    unit: expert.unit ?? expert.unit?._id ?? expert.unit?.id,
    units: Array.isArray(expert.units)
      ? expert.units.map((unit) => (typeof unit === 'string' ? unit : unit?._id ?? unit?.id)).filter(Boolean)
      : undefined,
    email: expert.email,
    phone: expert.phone,
    notes: expert.notes,
    language: expert.language,
    gender: expert.gender,
    archived: expert.archived,
    tenantIdDisc: expert.tenantIdDisc,
    customFields: expert.customFields,
    __v: expert.__v,
    createdAt: expert.createdAt,
    updatedAt: expert.updatedAt,
  };
};

export const mergeOrgs = async (toBeMergedOrgs, targetOrg, targetOrgJournalists) => {
  try {
    const normalizedToBeMergedOrgs = (toBeMergedOrgs ?? []).map((org) => ({
      _id: org?._id ?? org,
    }));
    const normalizedTargetOrg = { _id: targetOrg?._id ?? targetOrg };
    const res = await API.put(`/journalists/mergeOrgs`, {
      toBeMergedOrgs: normalizedToBeMergedOrgs,
      targetOrg: normalizedTargetOrg,
      targetOrgJournalists,
    });
    return res;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const mergeJournalists = async (mergingJournalists, targetJournalist) => {
  try {
    const res = await API.put(`/issues/mergeJournalists`, {
      mergingJournalists: (mergingJournalists || []).map((journalist) => normalizeMergeJournalist(journalist)),
      targetJournalist: normalizeMergeJournalist(targetJournalist),
    });
    return res;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const mergeExperts = async (mergingExperts, targetExpert) => {
  try {
    const resIssues = await API.put(`/issues/mergeExperts`, {
      mergingExperts: (mergingExperts || []).map((expert) => normalizeMergeExpert(expert)),
      targetExpert: normalizeMergeExpert(targetExpert),
    });
    const resEngagements = await API.put(`/engagements/mergeExperts`, {
      mergingExperts: (mergingExperts || []).map((expert) => normalizeMergeExpert(expert)),
      targetExpert: normalizeMergeExpert(targetExpert),
    });

    const normalizedMergingExperts = (mergingExperts || [])
      .map((expert) => {
        const id = normalizeServiceId(expert);
        return id ? { _id: id } : null;
      })
      .filter(Boolean);

    const targetId = normalizeServiceId(targetExpert);
    if (normalizedMergingExperts.length === 0 || !targetId) {
      throw new Error('invalid service merge payload');
    }

    const servicePayload = { mergingExperts: normalizedMergingExperts, targetExpert: { _id: targetId } };
    const resServices = await API.put(`/services/mergeExperts`, servicePayload);
    if (
      resIssues.status === 200 &&
      resServices.status === 200 &&
      (resEngagements.status === 200 || resEngagements.status === 403)
    ) {
      return { status: 200, message: 'Experts merged successfully' };
    }
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const archiveJournalist = async (journalistId) => {
  try {
    const res = await API.put(`/journalist/${journalistId}/archive`);
    return res;
  } catch (error) {
    console.error(`failed to archive journalist: ${journalistId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const unarchiveJournalist = async (journalistId) => {
  try {
    const res = await API.put(`/journalist/${journalistId}/unarchive`);
    return res;
  } catch (error) {
    console.error(`failed to unarchive journalist: ${journalistId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const archiveExpert = async (expertId) => {
  try {
    const normalizedId = normalizeExpertId(expertId) ?? expertId;
    const res = await API.put(`/expert/${normalizedId}/archive`);
    return res;
  } catch (error) {
    console.error(`failed to archive expert: ${expertId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const unarchiveExpert = async (expertId) => {
  try {
    const normalizedId = normalizeExpertId(expertId) ?? expertId;
    const res = await API.put(`/expert/${normalizedId}/unarchive`);
    return res;
  } catch (error) {
    console.error(`failed to unarchive expert: ${expertId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const archiveOrg = async (orgId) => {
  try {
    const res = await API.put(`/org/${orgId}/archive`);
    return res;
  } catch (error) {
    console.error(`failed to archive organization: ${orgId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const unarchiveOrg = async (orgId) => {
  try {
    const res = await API.put(`/org/${orgId}/unarchive`);
    return res;
  } catch (error) {
    console.error(`failed to unarchive organization: ${orgId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const getCommunityMembers = async () => {
  try {
    const res = await API.get(`/community-members`);
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getOrganizations = async () => {
  try {
    const res = await API.get('/organizations');
    return res.data;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const getPersonnelByOrganization = async (outletID) => {
  const URL = `/community-members?Organization=${outletID}`;
  const res = await API.get(URL);
  res.data?.data?.sort((dataA, dataB) => {
    if (dataA.name?.toLowerCase() < dataB.name?.toLowerCase()) {
      return -1;
    }
    if (dataA.name?.toLowerCase() > dataB.name?.toLowerCase()) {
      return 1;
    }
    return 0;
  });
  return res.data;
};

export const getOrganizationsByCaseInsensitiveName = async (organizationName) => {
  const normalizedName = normalizeOrganizationQueryName(organizationName);
  const res = await API.get(`/organizations?name=${normalizedName}&caseInsensitiveName=true`);
  return res.data;
};

export const getCommunityMembersByCaseInsensitiveNameAndOrganization = async (communityMemberName, organization) => {
  const res = await API.get(
    `/community-members?name=${communityMemberName}&Organization=${organization}&caseInsensitiveName=true`
  );
  return res.data;
};

export const mergeOrganizations = async (
  toBeMergedOrganizations,
  targetOrganization,
  targetOrganizationCommunityMembers
) => {
  try {
    const normalizeOrgRef = (org) => {
      if (!org) return org;
      if (typeof org === 'string') return { _id: org };
      if (org._id) return { _id: org._id };
      if (org.id) return { _id: org.id };
      return org;
    };
    const normalizedToBeMergedOrganizations = (toBeMergedOrganizations || []).map(normalizeOrgRef);
    const normalizedTargetOrganization = normalizeOrgRef(targetOrganization);

    const res = await API.put(`/community-members/mergeOrganizations`, {
      toBeMergedOrganizations: normalizedToBeMergedOrganizations,
      targetOrganization: normalizedTargetOrganization,
      targetOrganizationCommunityMembers,
    });
    return res;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const mergeCommunityMembers = async (mergingCommunityMembers, targetCommunityMember) => {
  try {
    const normalizeOrganizationId = (member) => ({
      ...member,
      Organization: member?.Organization?._id ?? member?.Organization,
    });
    const res = await API.put(`/engagements/mergeCommunityMembers`, {
      mergingCommunityMembers: (mergingCommunityMembers || []).map(normalizeOrganizationId),
      targetCommunityMember: normalizeOrganizationId(targetCommunityMember),
    });
    return res;
  } catch (error) {
    return { error: 'Failed request', message: error.message };
  }
};

export const archiveCommunityMember = async (communityMemberId) => {
  try {
    const res = await API.put(`/community-member/${communityMemberId}/archive`);
    return res;
  } catch (error) {
    console.error(`failed to archive community member: ${communityMemberId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const unarchiveCommunityMember = async (communityMemberId) => {
  try {
    const res = await API.put(`/community-member/${communityMemberId}/unarchive`);
    return res;
  } catch (error) {
    console.error(`failed to unarchive journalist: ${communityMemberId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const archiveOrganization = async (organizationId) => {
  try {
    const res = await API.put(`/organization/${organizationId}/archive`);
    return res;
  } catch (error) {
    console.error(`failed to archive organization: ${organizationId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const unarchiveOrganization = async (organizationId) => {
  try {
    const res = await API.put(`/organization/${organizationId}/unarchive`);
    return res;
  } catch (error) {
    console.error(`failed to unarchive organization: ${organizationId}`, error);
    return { error: 'Failed request', message: error.message };
  }
};

export const getCustomFieldConfigs = async (model) => {
  try {
    const res = await API.get(`/custom-field-config/${model}`);
    return res.data.filter((config) => config.enabled === true);
  } catch (e) {
    throw new Error(e.response?.data?.message ?? 'Failed to get custom field configs');
  }
};
